import { apiFetch, apiFetchBlob, apiFetchText } from "./api";
import type { PagedResult } from "./clients-accounts";

// ============================================================================
// DTOs (mirror src/Allmark.Contracts/Jobs/*.cs)
//
// Wire-format casing: ASP.NET Core System.Text.Json default = camelCase.
// Type adaptations:
//   - decimal → number.
//   - DateTime / DateTime? → string (ISO 8601 from JSON).
//   - DateOnly / DateOnly? → string (ISO date from JSON).
//   - IReadOnlyList<T> → T[].
// ============================================================================

// ----------------------------------------------------------------------------
// Job list / detail
// ----------------------------------------------------------------------------

export interface JobListItemDto {
  jobId: number;
  jobReferenceNo: string;
  clientName: string | null;
  ownerEmployeeName: string | null;
  currentStatusCode: string;
  statusColor: string | null;
  currentStatusEnteredUtc: string;
  expectedDispatchDate: string | null;
  totalIncGst: number | null;
  convertedFromQuoteId: number | null;
  waitingOnClient: boolean;
}

// Plan III — delivery editing
export interface JobDeliveryDto {
  deliveryAddressId: number | null;
  deliveryMethodId: number | null;
  deliveryTo: string | null;
  deliveryAttn: string | null;
  deliveryPhone: string | null;
  deliverySpecialInstructions: string | null;
  toBeDelivered: boolean;
  collectByCourier: boolean;
}

export interface UpdateJobDeliveryRequest extends JobDeliveryDto {
  rowVersionBase64: string;
}

/**
 * What the caller will do with a parcel-label response. The server records a job event either
 * way, so this is what keeps a diagnostic download out of the job's dispatch history.
 * Mirrors the API's `?disposition=` values; `"print"` is the server default when omitted.
 */
export type StickerDisposition = "print" | "download";

// Plan III — print / dispatch
export interface PrintHintDto {
  paperSize: string;
  orientation: string;
  marginsCss: string;
  suggestedTitle: string;
}

export interface PrintDeliveryDocketRequest {
  parcelCount: number;
  itemSummary: string;
  courier: string | null;
  trackingNumber: string | null;
  dispatchedDate: string | null; // ISO yyyy-mm-dd
  markDispatched: boolean;
  rowVersionBase64: string;
}

export interface PrintDeliveryDocketResponse {
  pdfUrl: string;
  sequence: number;
  jobStatus: string;
  dispatchedAtUtc: string | null;
}

export interface PrintJobSheetResponse {
  pdfUrl: string;
  sequence: number;
}

export interface JobDetailDto {
  jobId: number;
  jobReferenceNo: string;
  clientId: number | null;
  clientName: string | null;
  addressId: number | null;
  companyId: number | null;
  branchId: number | null;
  ownerEmployeeId: number | null;
  ownerEmployeeName: string | null;
  description: string | null;
  customerReference: string | null;
  purchaseOrderNumber: string | null;
  expectedDispatchDate: string | null;
  currentStatusCode: string;
  statusColor: string | null;
  currentStatusEnteredUtc: string;
  totalExGst: number | null;
  gst: number | null;
  totalIncGst: number | null;
  completedAtUtc: string | null;
  cancelledAtUtc: string | null;
  cancelReason: string | null;
  invoicedAtUtc: string | null;
  generatedInvoiceId: number | null;
  convertedFromQuoteId: number | null;
  rowVersionBase64: string | null;
  // Plan III — delivery + dispatch
  delivery: JobDeliveryDto | null;
  dispatchedAtUtc: string | null;
  dispatchCourier: string | null;
  dispatchTrackingNumber: string | null;
  dispatchParcelCount: number | null;
  dispatchItemSummary: string | null;
  currentJobSheetBlobPath: string | null;
  currentDeliveryDocketBlobPath: string | null;
  waitingOnClient: boolean;
}

// ----------------------------------------------------------------------------
// Job items
// ----------------------------------------------------------------------------

export interface JobItemDto {
  jobItemId: number;
  jobId: number;
  sortOrder: number;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  gstRate: number;
  lineTotalExGst: number;
  lineTotalIncGst: number;
  mfgBomId: number | null;
  rowVersionBase64: string | null;
}

export interface CreateJobItemRequest {
  sortOrder?: number | null;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  gstRate?: number;
}

export interface UpdateJobItemRequest {
  sortOrder: number;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  gstRate: number;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Job create / update
// ----------------------------------------------------------------------------

export interface CreateJobRequest {
  clientId?: number | null;
  addressId?: number | null;
  companyId?: number | null;
  branchId?: number | null;
  ownerEmployeeId?: number | null;
  description?: string | null;
  customerReference?: string | null;
  purchaseOrderNumber?: string | null;
  expectedDispatchDate?: string | null;
  dueDate?: string | null;
  items?: CreateJobItemRequest[] | null;
}

export interface UpdateJobRequest {
  clientId?: number | null;
  addressId?: number | null;
  companyId?: number | null;
  branchId?: number | null;
  ownerEmployeeId?: number | null;
  description?: string | null;
  customerReference?: string | null;
  purchaseOrderNumber?: string | null;
  expectedDispatchDate?: string | null;
  dueDate?: string | null;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Notes
// ----------------------------------------------------------------------------

export interface UpdateNotesRequest {
  note?: string | null;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Email
// ----------------------------------------------------------------------------

export type ReplyToType =
  | "CurrentUser"
  | "RequestingEmployee"
  | "NoReply"
  | "GeneralInbox";

export interface SendJobEmailRequest {
  toEmailAddress: string;
  cc?: string | null;
  bcc?: string | null;
  subject: string;
  htmlBody: string;
  replyToType: ReplyToType;
  attachmentJobFileIds?: number[] | null;
  flagWaitingOnClient: boolean;
}

export interface ToggleWaitingOnClientRequest {
  waiting: boolean;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Status transitions
// ----------------------------------------------------------------------------

export interface LegalTransitionDto {
  targetStatusCode: string;
  targetStatusLabel: string;
  color: string | null;
  requiresWriteClaim: boolean;
  isSystemOnly: boolean;
}

export interface ChangeStatusRequest {
  targetStatusCode: string;
  comment?: string | null;
  rowVersionBase64: string;
  idempotencyKey?: string | null;
}

export interface ChangeStatusResultDto {
  jobId: number;
  previousStatusCode: string;
  newStatusCode: string;
  eventId: number;
}

// ----------------------------------------------------------------------------
// Events
// ----------------------------------------------------------------------------

export interface JobEventDto {
  jobEventId: number;
  jobId: number;
  eventKind: string;
  fromStatusCode: string | null;
  toStatusCode: string | null;
  comment: string | null;
  occurredOnUtc: string;
  actorUserName: string;
}

// ----------------------------------------------------------------------------
// Status codes (admin lookup)
// ----------------------------------------------------------------------------

export interface JobStatusCodeDto {
  statusCode: string;
  shortDesc: string;
  longDesc: string | null;
  sortOrder: number;
  color: string | null;
  isTerminal: boolean;
  defaultNextStateCode: string | null;
  stuckAfterDays: number | null;
  statusGroupCode: string | null;
  deleted: boolean;
  jobsUsingCount: number;
}

export interface CreateJobStatusCodeRequest {
  statusCode: string;
  shortDesc: string;
  longDesc?: string | null;
  sortOrder?: number;
  color?: string | null;
  isTerminal?: boolean;
  defaultNextStateCode?: string | null;
  stuckAfterDays?: number | null;
  statusGroupCode?: string | null;
}

export interface UpdateJobStatusCodeRequest {
  shortDesc: string;
  longDesc?: string | null;
  sortOrder?: number;
  color?: string | null;
  isTerminal?: boolean;
  defaultNextStateCode?: string | null;
  stuckAfterDays?: number | null;
  statusGroupCode?: string | null;
}

// ----------------------------------------------------------------------------
// List query params
// ----------------------------------------------------------------------------

export interface ListJobsParams {
  status?: string | string[];
  clientId?: number;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Convenience type aliases used by SPA components
// ============================================================================

export type JobListItem = JobListItemDto;
export type JobDetail = JobDetailDto;
export type JobItem = JobItemDto;
export type JobEvent = JobEventDto;
export type LegalTransition = LegalTransitionDto;
export type JobStatusCode = JobStatusCodeDto;

// ============================================================================
// Content-lock helper
//
// When a job reaches one of these statuses the backend rejects content
// mutations (description, delivery, special notes, BOM items) with 409.
// Gate all edit entry-points on `!isJobContentLocked(job.currentStatusCode)`
// so the user never reaches that error state.
//
// Editable statuses: Pending, InProgress, OnHold, AwaitingArtwork,
//                    AwaitingApproval
// Locked  statuses: ReadyForProduction, InProduction, Dispatched, Completed,
//                   Invoiced, Cancelled
// ============================================================================

export const LOCKED_JOB_STATUSES = new Set([
  "ReadyForProduction",
  "InProduction",
  "Dispatched",
  "Completed",
  "Invoiced",
  "Cancelled",
]);

/** Returns `true` when the job's current status makes content read-only. */
export function isJobContentLocked(statusCode: string): boolean {
  return LOCKED_JOB_STATUSES.has(statusCode);
}

// ============================================================================
// Query-string helper
// ============================================================================

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.append(k, String(v));
    }
  }
  return sp.toString();
}

// ============================================================================
// API helpers
// ============================================================================

export const jobsApi = {
  // ---- Jobs -------------------------------------------------------------------

  list: (params: ListJobsParams = {}): Promise<PagedResult<JobListItemDto>> => {
    const search = qs(params as Record<string, unknown>);
    return apiFetch<PagedResult<JobListItemDto>>(`/api/jobs${search ? `?${search}` : ""}`);
  },

  get: (id: number): Promise<JobDetailDto> =>
    apiFetch<JobDetailDto>(`/api/jobs/${id}`),

  create: (body: CreateJobRequest): Promise<JobDetailDto> =>
    apiFetch<JobDetailDto>("/api/jobs", { method: "POST", body: JSON.stringify(body) }),

  update: (id: number, body: UpdateJobRequest): Promise<void> =>
    apiFetch<void>(`/api/jobs/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  remove: (id: number): Promise<void> =>
    apiFetch<void>(`/api/jobs/${id}`, { method: "DELETE" }),

  // ---- Job items --------------------------------------------------------------

  items: (id: number): Promise<JobItemDto[]> =>
    apiFetch<JobItemDto[]>(`/api/jobs/${id}/items`),

  createItem: (id: number, body: CreateJobItemRequest): Promise<JobItemDto> =>
    apiFetch<JobItemDto>(`/api/jobs/${id}/items`, { method: "POST", body: JSON.stringify(body) }),

  updateItem: (id: number, itemId: number, body: UpdateJobItemRequest): Promise<void> =>
    apiFetch<void>(`/api/jobs/${id}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteItem: (id: number, itemId: number): Promise<void> =>
    apiFetch<void>(`/api/jobs/${id}/items/${itemId}`, { method: "DELETE" }),

  // ---- Notes ------------------------------------------------------------------

  getNotes: (id: number): Promise<{ note: string | null; rowVersionBase64: string | null }> =>
    apiFetch<{ note: string | null; rowVersionBase64: string | null }>(`/api/jobs/${id}/notes`),

  putNotes: (id: number, body: UpdateNotesRequest): Promise<void> =>
    apiFetch<void>(`/api/jobs/${id}/notes`, { method: "PUT", body: JSON.stringify(body) }),

  getExtraNotes: (id: number): Promise<{ extraNote: string | null; rowVersionBase64: string | null }> =>
    apiFetch<{ extraNote: string | null; rowVersionBase64: string | null }>(`/api/jobs/${id}/extra-notes`),

  putExtraNotes: (id: number, body: UpdateNotesRequest): Promise<void> =>
    apiFetch<void>(`/api/jobs/${id}/extra-notes`, { method: "PUT", body: JSON.stringify(body) }),

  // ---- Events -----------------------------------------------------------------

  events: (id: number): Promise<JobEventDto[]> =>
    apiFetch<JobEventDto[]>(`/api/jobs/${id}/events`),

  // ---- Status transitions -----------------------------------------------------

  legalTransitions: (id: number): Promise<LegalTransitionDto[]> =>
    apiFetch<LegalTransitionDto[]>(`/api/jobs/${id}/legal-transitions`),

  changeStatus: (id: number, body: ChangeStatusRequest): Promise<ChangeStatusResultDto> =>
    apiFetch<ChangeStatusResultDto>(`/api/jobs/${id}/status`, { method: "POST", body: JSON.stringify(body) }),

  // ---- Invoice generation -----------------------------------------------------

  generateInvoice: (id: number): Promise<{ jobId: number; invoiceId: number }> =>
    apiFetch<{ jobId: number; invoiceId: number }>(`/api/jobs/${id}/generate-invoice`, { method: "POST" }),

  // ---- Plan III: delivery + documents -----------------------------------------

  updateDelivery: (id: number, body: UpdateJobDeliveryRequest): Promise<JobDeliveryDto> =>
    apiFetch<JobDeliveryDto>(`/api/jobs/${id}/delivery`, { method: "PATCH", body: JSON.stringify(body) }),

  printDeliveryDocket: (id: number, body: PrintDeliveryDocketRequest): Promise<PrintDeliveryDocketResponse> =>
    apiFetch<PrintDeliveryDocketResponse>(`/api/jobs/${id}/delivery-docket/print`, { method: "POST", body: JSON.stringify(body) }),

  printJobSheet: (id: number): Promise<PrintJobSheetResponse> =>
    apiFetch<PrintJobSheetResponse>(`/api/jobs/${id}/job-sheet/print`, { method: "POST" }),

  printDeliverySticker: (id: number, parcelCount: number | null): Promise<Blob> =>
    apiFetchBlob(`/api/jobs/${id}/delivery-sticker/print`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parcelCount }),
    }),

  /**
   * Same labels as `printDeliverySticker`, rendered as native ZPL instead of a PDF —
   * for the workstation-agent path, which sends the bytes straight to a label printer.
   * PDF stays the default and the fallback when no agent is present.
   *
   * `disposition` says what the caller will do with the bytes, because the server records a job
   * event either way: omitted (or `"print"`) is a real dispatch, `"download"` is the diagnostic
   * that saves the ZPL to a file. Without it every diagnostic download would appear in the job's
   * visible history as a parcel print that never happened.
   */
  printDeliveryStickerZpl: (
    id: number,
    parcelCount: number | null,
    disposition?: StickerDisposition,
  ): Promise<string> =>
    apiFetchText(
      `/api/jobs/${id}/delivery-sticker/print?format=zpl${disposition ? `&disposition=${disposition}` : ""}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parcelCount }),
      },
    ),

  printHint: (id: number, kind: "JobSheet" | "DeliveryDocket" | "DeliverySticker"): Promise<PrintHintDto> =>
    apiFetch<PrintHintDto>(`/api/jobs/${id}/print-hint?kind=${kind}`),

  // ---- Email ------------------------------------------------------------------

  sendEmail: (jobId: number, body: SendJobEmailRequest) =>
    apiFetch<{ emailLogId: number; jobEmailId: number | null; sentSuccessfully: boolean }>(
      `/api/jobs/${jobId}/emails`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  toggleWaitingOnClient: (jobId: number, body: ToggleWaitingOnClientRequest) =>
    apiFetch<void>(`/api/jobs/${jobId}/waiting-on-client`, {
      method: "PATCH", body: JSON.stringify(body),
    }),

  // ---- BOM Plan II — add items from product ----------------------------------

  itemsFromProduct: (jobId: number, body: { productId: number; bomTemplateId: number | null; quantity: number | null }): Promise<{ bomId: number; item: JobItemDto }> =>
    apiFetch<{ bomId: number; item: JobItemDto }>(`/api/jobs/${jobId}/items-from-product`, { method: "POST", body: JSON.stringify(body) }),

  // ---- Status codes (admin lookup) --------------------------------------------

  statusCodes: {
    list: (): Promise<JobStatusCodeDto[]> =>
      apiFetch<JobStatusCodeDto[]>("/api/job-status-codes"),

    get: (code: string): Promise<JobStatusCodeDto> =>
      apiFetch<JobStatusCodeDto>(`/api/job-status-codes/${encodeURIComponent(code)}`),

    create: (body: CreateJobStatusCodeRequest): Promise<JobStatusCodeDto> =>
      apiFetch<JobStatusCodeDto>("/api/job-status-codes", { method: "POST", body: JSON.stringify(body) }),

    update: (code: string, body: UpdateJobStatusCodeRequest): Promise<void> =>
      apiFetch<void>(`/api/job-status-codes/${encodeURIComponent(code)}`, { method: "PATCH", body: JSON.stringify(body) }),

    remove: (code: string): Promise<void> =>
      apiFetch<void>(`/api/job-status-codes/${encodeURIComponent(code)}`, { method: "DELETE" }),
  },
};

